// --------------------------------------------------------------
// Created On: 2026-11-03
// Author: Zachary Thomas
//
// Last Modified: 2026-11-03
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import styles from "./SystemControlComponentGaugeSpeedometer.module.scss";

// Speedometer graphic with a needle that turns across the dial, with its value, minimum, and maximum overlaid.
export default function SystemControlComponentGaugeSpeedometer(props: Props): Component {
  // Returns the custom properties that drive the needle position and the highlight color of the graphic.
  function getSpeedometerStyle(): React.CSSProperties {
    const speedometerStyle: { [key: string]: string | number } = { "--gauge-fill-percent": props.fillPercent };
    if (props.highlightColor !== null) {
      speedometerStyle["--gauge-needle-color"] = props.highlightColor;
      speedometerStyle["--gauge-tint-color"] = props.highlightColor;
    }
    return speedometerStyle as React.CSSProperties;
  }

  // Returns the class names for the graphic.
  function getGraphicClassName(): string {
    if (props.highlightColor !== null) {
      return `${styles.graphic} ${styles.highlighted}`;
    } else {
      return styles.graphic;
    }
  }

  return (
    <div className={styles.speedometer} style={getSpeedometerStyle()}>
      <div className={getGraphicClassName()}>
        <svg className={styles.svg} viewBox="0 0 176 176" xmlns="http://www.w3.org/2000/svg">
          <circle className={styles.background} cx="88" cy="88" r="86" />
          <circle className={styles.tint} cx="88" cy="88" r="86" />
          <circle className={styles.outline} cx="88" cy="88" r="86" />
          <path
            className={styles.tick}
            d="M15.5067 102.93C15.4399 102.603 15.3752 102.276 15.3127 101.949L11.4012 102.727L11.5963 103.707L15.5067 102.93Z"
          />
          <path
            className={styles.tick}
            d="M15.3126 74.0515C15.3752 73.7237 15.4398 73.3967 15.5067 73.0705L11.5963 72.2927L11.4012 73.2735L15.3126 74.0515Z"
          />
          <path
            className={styles.tick}
            d="M26.188 47.2998C26.3717 47.0215 26.5571 46.7445 26.7444 46.4689L23.4232 44.2497L22.8676 45.0812L26.188 47.2998Z"
          />
          <path
            className={styles.tick}
            d="M46.4689 26.7443C46.7446 26.5571 47.0216 26.3716 47.2999 26.188L45.0813 22.8676L44.2498 23.4232L46.4689 26.7443Z"
          />
          <path
            className={styles.tick}
            d="M73.0704 15.5067C73.3966 15.4398 73.7236 15.3751 74.0514 15.3126L73.2733 11.4011L72.2926 11.5962L73.0704 15.5067Z"
          />
          <path
            className={styles.tick}
            d="M101.949 15.3126C102.276 15.3751 102.603 15.4398 102.93 15.5067L103.707 11.5964L102.727 11.4013L101.949 15.3126Z"
          />
          <path
            className={styles.tick}
            d="M128.7 26.188C128.978 26.3716 129.255 26.557 129.531 26.7443L131.75 23.4231L130.919 22.8675L128.7 26.188Z"
          />
          <path
            className={styles.tick}
            d="M149.256 46.469C149.443 46.7446 149.628 47.0216 149.812 47.2999L153.132 45.0813L152.577 44.2498L149.256 46.469Z"
          />
          <path
            className={styles.tick}
            d="M160.493 73.0704C160.56 73.3966 160.625 73.7236 160.687 74.0514L164.599 73.2733L164.404 72.2926L160.493 73.0704Z"
          />
          <path
            className={styles.tick}
            d="M160.687 101.949C160.625 102.276 160.56 102.603 160.493 102.93L164.404 103.708L164.599 102.727L160.687 101.949Z"
          />
          <path
            className={styles.tick}
            d="M16.0017 88.5C16.0006 88.3335 16 88.1668 16 88C16 87.8332 16.0006 87.6665 16.0017 87.5H10V88.5H16.0017Z"
          />
          <path
            className={styles.tick}
            d="M36.7359 37.4431C36.97 37.2058 37.2057 36.9701 37.443 36.736L33.1992 32.4922L32.4921 33.1993L36.7359 37.4431Z"
          />
          <path
            className={styles.tick}
            d="M87.5 16.0017C87.6665 16.0006 87.8332 16 88 16C88.1668 16 88.3335 16.0006 88.5 16.0017V10H87.5V16.0017Z"
          />
          <path
            className={styles.tick}
            d="M138.557 36.736C138.794 36.9701 139.03 37.2058 139.264 37.4431L143.508 33.1992L142.801 32.4921L138.557 36.736Z"
          />
          <path
            className={styles.tick}
            d="M159.998 87.5C159.999 87.6665 160 87.8332 160 88C160 88.1668 159.999 88.3335 159.998 88.5H166V87.5H159.998Z"
          />
          <path
            className={styles.tick}
            d="M23.5047 115.256C23.3749 114.949 23.2472 114.641 23.1217 114.332L15.746 117.387L16.1287 118.311L23.5047 115.256Z"
          />
          <path
            className={styles.tick}
            d="M23.1216 61.6677C23.2472 61.3587 23.3749 61.0508 23.5047 60.744L16.1288 57.6888L15.7461 58.6127L23.1216 61.6677Z"
          />
          <path
            className={styles.tick}
            d="M60.744 23.5046C61.0508 23.3748 61.3587 23.2471 61.6677 23.1216L58.6127 15.746L57.6888 16.1287L60.744 23.5046Z"
          />
          <path
            className={styles.tick}
            d="M114.332 23.1216C114.641 23.2471 114.949 23.3748 115.256 23.5046L118.311 16.1288L117.387 15.7461L114.332 23.1216Z"
          />
          <path
            className={styles.tick}
            d="M152.495 60.744C152.625 61.0508 152.753 61.3587 152.878 61.6677L160.254 58.6127L159.871 57.6888L152.495 60.744Z"
          />
          <path
            className={styles.tick}
            d="M152.878 114.332C152.753 114.641 152.625 114.949 152.495 115.256L159.871 118.311L160.254 117.387L152.878 114.332Z"
          />
          <circle className={styles.tick} cx="88" cy="88" r="8" />
          <path
            className={styles.needle}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M105.386 87.0484L94.3201 86.4649L25.5584 114.945L24.793 113.098L93.5548 84.6171L100.967 76.3803L106.872 80.1837L105.386 87.0484Z"
          />
          <circle className={styles.pin} cx="88" cy="88" r="4" />
        </svg>
        <div className={styles.overlay}>
          <div data-test="system-control-gauge-value" className={styles.value}>
            {props.value === null && "N/A"}
            {props.value !== null && props.value}
            {props.value !== null && props.unitShortName !== null && (
              <span className={styles.units}>{props.unitShortName}</span>
            )}
          </div>
        </div>
        <span data-test="system-control-gauge-min" className={`${styles.scale} ${styles.scaleMin}`}>
          {props.min}
        </span>
        <span data-test="system-control-gauge-max" className={`${styles.scale} ${styles.scaleMax}`}>
          {props.max}
        </span>
      </div>
    </div>
  );
}

interface Props {
  value: string | null;
  unitShortName: string | null;
  fillPercent: number;
  highlightColor: string | null;
  min: number | string;
  max: number | string;
}
